import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { AuthShell } from '@/components/auth/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/lib/api';

// ── Flash banner ──────────────────────────────────────────────────────────────

type FlashKind = 'email_verified' | 'password_reset' | 'session_expired';

function FlashBanner({ kind, onDismiss }: { kind: FlashKind; onDismiss: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted px-4 py-3 text-sm text-foreground"
    >
      <span>{t(`auth.flash.${kind}`)}</span>
      <button
        type="button"
        aria-label={t('auth.flash.dismiss')}
        onClick={onDismiss}
        className="shrink-0 rounded p-0.5 hover:bg-muted-foreground/10 transition-colors"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

const loginSchema = z.object({
  email: z.string().min(1, { message: 'common.required' }).email({ message: 'auth.email_invalid' }),
  password: z.string().min(1, { message: 'common.required' }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const locationState = location.state as {
    from?: { pathname: string };
    flash?: FlashKind;
  } | null;
  const [flash, setFlash] = useState<FlashKind | null>(locationState?.flash ?? null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const fromPath = locationState?.from?.pathname || '/';

  async function onSubmit(values: LoginFormValues) {
    setSubmitError(null);
    try {
      await login(values.email, values.password);
      // Honor an explicit redirect path (e.g. from ProtectedRoute), else land
      // on the dashboard. The dashboard CTA card shows what's pending.
      navigate(fromPath, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSubmitError(t('auth.invalid_credentials'));
      } else {
        setSubmitError(t('common.error_generic'));
      }
    }
  }

  return (
    <AuthShell
      title={t('auth.login_title')}
      subtitle={t('auth.login_subtitle')}
      footer={
        <>
          {t('auth.no_account')}{' '}
          <Link
            to="/signup"
            className="text-foreground font-medium underline-offset-4 hover:underline"
          >
            {t('auth.create_account')}
          </Link>
        </>
      }
    >
      {flash && <FlashBanner kind={flash} onDismiss={() => setFlash(null)} />}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.email')}</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="email" autoFocus className="h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>{t('auth.password')}</FormLabel>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t('auth.forgot_password')}
                  </Link>
                </div>
                <FormControl>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    className="h-11"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {submitError && (
            <div role="alert" className="text-sm text-destructive">
              {submitError}
            </div>
          )}

          <Button type="submit" className="w-full h-11" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? t('common.loading') : t('auth.login_button')}
          </Button>
        </form>
      </Form>
    </AuthShell>
  );
}
